"use client";

import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { EASE_OUT_EXPO } from "@/components/motion/constants";
import { useTheme } from "@/components/theme/ThemeProvider";
import { cn } from "@/lib/utils";

interface NavPillProps {
  isScrolled: boolean;
  overlay: boolean;
  children: ReactNode;
  className?: string;
}

const SCROLL_TRANSITION = {
  duration: 0.45,
  ease: EASE_OUT_EXPO,
} as const;

export function NavPill({
  isScrolled,
  overlay,
  children,
  className,
}: NavPillProps) {
  const { theme } = useTheme();
  const reduced = useReducedMotion();
  const isLight = theme === "light";
  const isSolid = isScrolled || !overlay;

  return (
    <motion.div
      className={cn(
        "glass-pill-nav flex items-center justify-between rounded-full border transition-[gap,padding-inline] duration-[450ms]",
        isScrolled
          ? "gap-1 px-2 sm:gap-2 sm:px-3 md:px-4 lg:gap-1.5 xl:gap-3 xl:px-5"
          : "gap-1.5 px-2.5 sm:gap-3 sm:px-4 md:px-5 lg:gap-2 xl:gap-4 xl:px-6",
        overlay && "nav-pill-over-hero",
        className
      )}
      style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
      animate={{
        backgroundColor: isSolid
          ? isLight
            ? "rgba(255, 255, 255, 0.97)"
            : "rgba(20, 20, 20, 0.92)"
          : "rgba(255, 255, 255, 0)",
        borderColor: isSolid
          ? isLight
            ? "rgba(0, 0, 0, 0.06)"
            : "rgba(255, 255, 255, 0.1)"
          : isLight
            ? "rgba(0, 0, 0, 0.04)"
            : "rgba(255, 255, 255, 0.08)",
        boxShadow: isSolid
          ? isLight
            ? "0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)"
            : "0 8px 32px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.2)"
          : "0 0 0 rgba(0, 0, 0, 0)",
        paddingTop: isScrolled ? 6 : isSolid ? 8 : 10,
        paddingBottom: isScrolled ? 6 : isSolid ? 8 : 10,
      }}
      transition={reduced ? { duration: 0 } : SCROLL_TRANSITION}
    >
      {children}
    </motion.div>
  );
}
